
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Navbar from '../../components/Navbar';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const AdminAdvertisements = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        link: '',
        order: 0,
        is_active: true,
        image: null
    });
    const [editingId, setEditingId] = useState(null);

    const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : process.env.REACT_APP_API_URL;
    const STORAGE_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000' : process.env.REACT_APP_API_URL?.replace('/api', '');

    useEffect(() => {
        fetchAds();
    }, []);

    const fetchAds = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/advertisements`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAds(data);
            } else {
                toast.error('Failed to fetch advertisements');
            }
        } catch (error) {
            console.error('Error fetching ads:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('title', formData.title);
        data.append('content', formData.content); // Optional now
        if (formData.link) data.append('link', formData.link);
        data.append('order', formData.order);
        data.append('is_active', formData.is_active ? '1' : '0');
        if (formData.image) data.append('image', formData.image);

        try {
            let url = `${API_URL}/admin/advertisements`;
            // For updates with file upload, we use POST workaround or just POST if Laravel handles method spoofing, 
            // but standard FormData with file usually needs POST. 
            // However, Laravel resource update expects PUT/PATCH.
            // A common trick is POST with _method=PUT.

            if (editingId) {
                url = `${API_URL}/admin/advertisements/${editingId}`;
                data.append('_method', 'PUT');
            }

            const response = await fetch(url, {
                method: 'POST', // Always POST for FormData with files, use _method=PUT for updates
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                },
                body: data
            });

            if (response.ok) {
                toast.success(`Advertisement ${editingId ? 'updated' : 'added'} successfully`);
                setFormData({ title: '', content: '', link: '', order: 0, is_active: true, image: null });
                setEditingId(null);
                document.getElementById('ad-image-input').value = "";
                fetchAds();
            } else {
                const err = await response.json();
                const errorMessage = err.errors
                    ? Object.values(err.errors).flat().join('\n')
                    : (err.message || 'Failed to save advertisement');
                toast.error(errorMessage, { duration: 5000 });
            }
        } catch (error) {
            console.error('Error saving ad:', error);
            toast.error('Error saving advertisement');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this ad?')) return;
        try {
            const response = await fetch(`${API_URL}/admin/advertisements/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                }
            });
            if (response.ok) {
                toast.success('Advertisement deleted');
                fetchAds();
            } else {
                toast.error('Failed to delete ad');
            }
        } catch (error) {
            toast.error('Error deleting advertisement');
        }
    };

    const handleEdit = (ad) => {
        setEditingId(ad.id);
        setFormData({
            title: ad.title || '',
            content: ad.content || '',
            link: ad.link || '',
            order: ad.order || 0,
            is_active: ad.is_active,
            image: null
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ title: '', content: '', link: '', order: 0, is_active: true, image: null });
        document.getElementById('ad-image-input').value = "";
    }

    return (
        <div className="min-h-screen bg-cover bg-center bg-fixed" style={{ backgroundImage: "url('/images/trade-bg.jpg')" }}>
            <div className="min-h-screen bg-black/40 w-full pb-10">
                <Navbar userType="admin" />
                <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Logo Ticker (Advertisements)</h1>

                    {/* Form */}
                    <div className="bg-white shadow rounded-lg p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-4">{editingId ? 'Edit Advertisement' : 'Add New Advertisement'}</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Logo Image {editingId && '(Leave empty to keep current)'}</label>
                                    <input
                                        id="ad-image-input"
                                        type="file"
                                        name="image"
                                        accept="image/*"
                                        onChange={handleInputChange}
                                        required={!editingId}
                                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Title (Admin Reference)</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Description / Alt Text (Optional)</label>
                                    <textarea
                                        name="content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        rows="2"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Link URL (Where logo clicks to)</label>
                                    <input
                                        type="url"
                                        name="link"
                                        value={formData.link}
                                        onChange={handleInputChange}
                                        placeholder="https://"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                    />
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Order</label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                        />
                                    </div>
                                    <div className="flex items-center pt-6">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 block text-sm text-gray-900">Active</label>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3">
                                {editingId && (
                                    <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {editingId ? 'Update Advertisement' : 'Add Advertisement'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* List */}
                    {loading ? <p>Loading...</p> : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {ads.map((ad) => (
                                <div key={ad.id} className="bg-white rounded-lg shadow overflow-hidden relative group">
                                    <div className="aspect-w-3 aspect-h-2 h-32 bg-gray-200">
                                        {ad.image_path ? (
                                            <img
                                                src={`${STORAGE_URL}${ad.image_path}`}
                                                alt={ad.title}
                                                className="w-full h-full object-contain p-2"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-medium text-gray-900 truncate">{ad.title || 'Untitled'}</h3>
                                                <p className="text-xs text-gray-500 truncate">{ad.content}</p>
                                            </div>
                                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {ad.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {ad.link && (
                                            <a href={ad.link} target="_blank" rel="noopener noreferrer" className="block mt-2 text-xs text-blue-600 hover:text-blue-500 truncate">
                                                {ad.link}
                                            </a>
                                        )}
                                        <div className="mt-4 flex justify-end space-x-2">
                                            <button
                                                onClick={() => handleEdit(ad)}
                                                className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(ad.id)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded-full"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminAdvertisements;
